import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileWarning, ChevronLeft, ChevronRight, Search, RotateCcw, ExternalLink, FileText, AlertCircle } from 'lucide-react';
import { useToast } from '../../../context/ToastContext';
import { fetchMissingDocuments, errorMessage } from '../../../api/document/documentCentreApi';
import { formatDate, plural } from '../DocumentCentre/documentCentre.constants';
import styles from '../DocumentCentre/DocumentCentre.module.css';

/**
 * Missing travel documents (SUNSKY Documents spec v1.2, §14).
 *
 * Starts from bookings, so a booking without any document is listed too. It
 * names the missing component and the known cause; it creates nothing and is
 * not a task list. Documents are generated in the booking.
 */

const todayInBrussels = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Brussels', year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date()
  );

const CAUSE_COLOR = {
  supplier_confirmation: '#d97706',
  policy_not_issued: '#d97706',
  ticket_issuance: '#dc2626',
  not_available: '#475569',
  new_version: '#2563eb',
};

const MissingDocuments = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const initial = { q: '', dateFrom: todayInBrussels(), dateTo: '', cause: '' };
  const [draft, setDraft] = useState(initial);
  const [applied, setApplied] = useState(initial);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const requestId = useRef(0);

  useEffect(() => {
    const id = ++requestId.current;
    setLoading(true);
    setError('');
    fetchMissingDocuments({ ...applied, page, pageSize: 25 })
      .then((res) => {
        if (id === requestId.current) setResult(res);
      })
      .catch(async (err) => {
        if (id !== requestId.current) return;
        const message = await errorMessage(err, 'Could not load the missing documents');
        setError(message);
        showToast(message, 'error');
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
  }, [applied, page, showToast]);

  const apply = (next) => {
    setApplied(next);
    setPage(1);
  };
  const set = (key) => (e) => setDraft((prev) => ({ ...prev, [key]: e.target.value }));

  const rows = result?.data?.rows || [];
  const summary = result?.data?.summary || { components: 0, bookings: 0, causes: {} };
  const causes = result?.data?.causes || [];
  const pagination = result?.meta || { page: 1, totalPages: 1, total: 0 };

  const filterCause = (cause) => {
    const next = { ...applied, cause: applied.cause === cause ? '' : cause };
    setDraft(next);
    apply(next);
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.titleBlock}>
          <div className={styles.manager}>
            <FileWarning size={20} />
            Missing Travel Documents
            <span className={styles.managerCount}>{summary.components}</span>
          </div>
          <span className={styles.subtitle}>
            Booked travel services without a usable document, with the known cause. Generate documents in the booking;
            services that were not booked never count as missing.
          </span>
        </div>
        <div className={styles.toolbarLinks}>
          <button type="button" className={styles.linkBtn} onClick={() => navigate('/documents/vouchers')}>
            <FileText size={14} /> Vouchers
          </button>
        </div>
      </div>

      <div className={styles.summaryBar}>
        <div className={styles.summaryCard}>
          <span className={styles.summaryLabel}>Missing components</span>
          <span className={styles.summaryValue}>{summary.components}</span>
          <span className={styles.summarySub}>for the travel dates shown</span>
        </div>
        <div className={styles.summaryCard}>
          <span className={styles.summaryLabel}>Bookings</span>
          <span className={styles.summaryValue}>{summary.bookings}</span>
          <span className={styles.summarySub}>with at least one missing document</span>
        </div>
        {causes.map((cause) => (
          <button
            type="button"
            key={cause.value}
            className={`${styles.summaryCard} ${styles.summaryCardButton}`}
            onClick={() => filterCause(cause.value)}
            style={applied.cause === cause.value ? { borderColor: '#f5a51e' } : undefined}
          >
            <span className={styles.summaryLabel}>{cause.label}</span>
            <span className={styles.summaryValue} style={{ color: CAUSE_COLOR[cause.value] }}>
              {summary.causes?.[cause.value] ?? 0}
            </span>
            <span className={styles.summarySub}>components</span>
          </button>
        ))}
      </div>

      <form
        className={styles.filterPanel}
        onSubmit={(e) => {
          e.preventDefault();
          apply({ ...draft });
        }}
      >
        <div className={styles.filterRow}>
          <div className={`${styles.field} ${styles.fieldWide}`}>
            <span className={styles.fieldLabel}>Booking reference or customer</span>
            <input className={styles.input} type="search" value={draft.q} onChange={set('q')} placeholder="Search" />
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Travel date</span>
            <div className={styles.dateRange}>
              <input className={styles.input} type="date" value={draft.dateFrom} onChange={set('dateFrom')} aria-label="From" />
              <input className={styles.input} type="date" value={draft.dateTo} onChange={set('dateTo')} aria-label="To" />
            </div>
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Known cause</span>
            <select className={styles.select} value={draft.cause} onChange={set('cause')}>
              <option value="">Any cause</option>
              {causes.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.filterActions}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => {
                const reset = { q: '', dateFrom: todayInBrussels(), dateTo: '', cause: '' };
                setDraft(reset);
                apply(reset);
              }}
            >
              <RotateCcw size={14} /> Clear filters
            </button>
            <button type="submit" className={styles.primaryBtn} disabled={loading}>
              <Search size={14} /> Apply filters
            </button>
          </div>
        </div>
        {applied.dateFrom && !applied.dateTo && (
          <span className={styles.muted}>Showing travel from {formatDate(applied.dateFrom)} onwards. Clear the date to include past trips.</span>
        )}
      </form>

      <div className={styles.tableSection}>
        <div className={styles.tableCard}>
          {error && !rows.length ? (
            <div className={styles.errorBox}>
              <AlertCircle size={16} /> {error}
            </div>
          ) : loading && !rows.length ? (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner} />
              <span className={styles.loadingText}>Checking bookings...</span>
            </div>
          ) : !rows.length ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <FileWarning size={26} />
              </div>
              <div className={styles.emptyTitle}>No missing travel documents</div>
              <div className={styles.emptyHint}>Every booked service in this period has a usable document.</div>
            </div>
          ) : (
            <table className={styles.table} style={{ minWidth: 900 }}>
              <thead>
                <tr>
                  <th>Travel date</th>
                  <th>Booking</th>
                  <th>Missing component</th>
                  <th>Known cause</th>
                  <th>Existing document</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={`${row.bookingReference}-${row.productId}`}>
                    <td>{formatDate(row.travelDate)}</td>
                    <td>
                      <button type="button" className={styles.bookingLink} onClick={() => navigate(`/bookings/${row.bookingId}`)}>
                        {row.bookingReference}
                      </button>
                      <span className={styles.mutedBlock}>{row.customerName || '-'}</span>
                    </td>
                    <td>
                      <span className={styles.docType}>{row.component}</span>
                      {row.service && <span className={styles.mutedBlock}>{row.service}</span>}
                      {row.supplier && <span className={styles.mutedBlock}>{row.supplier}</span>}
                    </td>
                    <td>
                      <span className={styles.statusText} style={{ color: CAUSE_COLOR[row.causeCode] || '#475569' }}>
                        {row.cause}
                      </span>
                    </td>
                    <td>{row.documentNumber || <span className={styles.muted}>None</span>}</td>
                    <td>
                      <div className={styles.rowActions}>
                        <button type="button" className={styles.viewBtn} onClick={() => navigate(`/bookings/${row.bookingId}`)}>
                          <ExternalLink size={13} /> Open booking
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className={styles.footerBar}>
          <span className={styles.paginationInfo}>
            Page {pagination.page} of {pagination.totalPages} ({plural(pagination.total, 'component')})
          </span>
          <div className={styles.paginationControls}>
            <button
              type="button"
              className={styles.pageBtn}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pagination.page <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft size={14} />
            </button>
            <span className={styles.paginationInfo}>{pagination.page}</span>
            <button
              type="button"
              className={styles.pageBtn}
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={pagination.page >= pagination.totalPages}
              aria-label="Next page"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissingDocuments;
